// Mail — what EmmA read, and what she did with it.
//
// The page's job, in Jacky's words: her reading of every mailbox, shown so he can
// decide without opening mail. The API does the bucketing; this is the read.
import {authHeaders, fetchImpl} from './api';
import {env} from './env';

export type MailFact = {label: string; value: string};

export type MailItem = {
  id: string; mailbox: string; from: string; from_name: string | null; subject: string;
  summary: string; received_at: string | null; job_ref: string | null; tags: string[];
  facts: MailFact[]; lead: string; folder: string | null; rule_tag: string | null;
  rule_state: 'armed' | 'waiting' | 'none'; needs_attention: boolean; read_via: string | null;
  /** A person marked it dealt with. It leaves the queue and stays visible. */
  handled: boolean;
};

export type MailBoard = {
  items: MailItem[];
  counts: {all: number; needs: number; waiting: number; filed: number};
  boxes: {mailbox: string; count: number; needs: number}[];
  tags: {id: string; label: string; uses: number}[];
  rules: {tag: string; label: string; folder: string | null; enabled: boolean; action: string}[];
  proposed: {id: string; label: string; meaning: string}[];
  vocabulary_unavailable: boolean;
};

export async function loadMail(opts: {mailbox?: string; q?: string; days?: number} = {}): Promise<MailBoard> {
  const search = new URLSearchParams();
  if (opts.mailbox && opts.mailbox !== 'all') search.set('mailbox', opts.mailbox);
  if (opts.q) search.set('q', opts.q);
  if (opts.days) search.set('days', String(opts.days));
  const r = await fetchImpl(`${env.apiBase}/api/mail${search.size ? `?${search}` : ''}`, {headers: await authHeaders()});
  const body = await r.json();
  if (!r.ok) throw Error(body.error ?? 'Mail unavailable');
  return body.data as MailBoard;
}

/** Mark an email dealt with, or put it back in the queue. Reversible on purpose,
 *  which is what makes "Done" safe to click. */
export async function setHandled(id: string, handled: boolean): Promise<void> {
  const r = await fetchImpl(`${env.apiBase}/api/mail/${encodeURIComponent(id)}/handled`, {
    method: 'POST',
    headers: {...(await authHeaders()), 'content-type': 'application/json'},
    body: JSON.stringify({handled}),
  });
  if (!r.ok) throw Error((await r.json().catch(() => ({}))).error ?? 'That email could not be updated');
}

/** Arm a rule for a tag, creating it when the tag has none yet — the common case
 *  on the page, since she coins tags faster than rules get set. */
export async function armRule(tag: string, folder: string): Promise<void> {
  const r = await fetchImpl(`${env.apiBase}/api/mail/${encodeURIComponent(tag)}/rule`, {
    method: 'POST',
    headers: {...(await authHeaders()), 'content-type': 'application/json'},
    body: JSON.stringify({tag, folder}),
  });
  if (!r.ok) throw Error((await r.json().catch(() => ({}))).error ?? 'That rule could not be set');
}

/** Arm or park a cleanup rule. The move itself is done by the mailbox worker when
 *  mail next arrives — this only records the operator's decision. */
export async function setRule(tag: string, enabled: boolean): Promise<void> {
  const r = await fetchImpl(`${env.apiBase}/api/mail/rules/${encodeURIComponent(tag)}`, {
    method: 'POST',
    headers: {...(await authHeaders()), 'content-type': 'application/json'},
    body: JSON.stringify({enabled}),
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw Error(body.error ?? 'The rule could not be changed');
}
